import React, { useState, useEffect } from 'react';
import { Users, Check, X, UserCheck, Shield, Trash2, Key, Edit, Link, LinkOff } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // Per i contatori
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, active, rejected
  const [selectedUser, setSelectedUser] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Contatori per ogni stato
  const counts = {
    all: allUsers.length,
    pending: allUsers.filter(u => u.status === 'pending').length,
    active: allUsers.filter(u => u.status === 'active').length,
    rejected: allUsers.filter(u => u.status === 'rejected').length
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch ALL users first for counts
      const allUsersRes = await fetch(`${API_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!allUsersRes.ok) throw new Error('Errore caricamento utenti');
      const allUsersData = await allUsersRes.json();
      setAllUsers(allUsersData);
      
      // Filter locally based on current filter
      if (filter === 'all') {
        setUsers(allUsersData);
      } else {
        setUsers(allUsersData.filter(u => u.status === filter));
      }
      
      // Fetch employees for linking
      const empRes = await fetch(`${API_URL}/api/employees`);
      const empData = await empRes.json();
      setEmployees(empData);
      
      setLoading(false);
    } catch (err) {
      console.error('Error:', err);
      setLoading(false);
    }
  };

  const handleApprove = async (userId, role, employeeId) => {
    const token = localStorage.getItem('token');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/approve`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          role: role,
          employee_id: employeeId || null
        })
      });

      if (!res.ok) throw new Error('Errore approvazione');

      alert('Utente approvato con successo');
      setShowApprovalModal(false);
      fetchData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Sei sicuro di voler rifiutare questa registrazione?')) {
      return;
    }

    const token = localStorage.getItem('token');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/reject`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error('Errore rifiuto');

      alert('Utente rifiutato');
      fetchData();
    } catch (err) {
      alert(err.message);
    }
  };

  // Delete user
  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Sei sicuro di voler ELIMINARE definitivamente l'utente "${userName}"? Questa azione non può essere annullata.`)) {
      return;
    }

    const token = localStorage.getItem('token');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Errore eliminazione');
      }

      alert('Utente eliminato con successo');
      fetchData();
    } catch (err) {
      alert(err.message);
    }
  };

  // Change user role
  const handleChangeRole = async (userId, newRole) => {
    const token = localStorage.getItem('token');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role: newRole })
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Errore modifica ruolo');
      }

      alert('Ruolo modificato con successo');
      fetchData();
    } catch (err) {
      alert(err.message);
    }
  };

  // Reset user password
  const handleResetPassword = async (userId, userName) => {
    const newPassword = window.prompt(`Inserisci la nuova password per "${userName}":`);
    
    if (!newPassword) return;
    
    if (newPassword.length < 6) {
      alert('La password deve avere almeno 6 caratteri');
      return;
    }

    const token = localStorage.getItem('token');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/reset-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password: newPassword })
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Errore reset password');
      }

      alert('Password resettata con successo');
    } catch (err) {
      alert(err.message);
    }
  };

  const openApprovalModal = (user) => {
    setSelectedUser({
      ...user,
      selectedRole: 'employee',
      selectedEmployeeId: ''
    });
    setShowApprovalModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser({
      ...user,
      newRole: user.role,
      newEmployeeId: user.employee_id || ''
    });
    setShowEditModal(true);
  };

  // Link/unlink employee to user
  const handleLinkEmployee = async (userId, employeeId) => {
    const token = localStorage.getItem('token');
    
    try {
      if (employeeId) {
        // Link employee
        const res = await fetch(`${API_URL}/api/admin/users/${userId}/link-employee?employee_id=${employeeId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.detail || 'Errore collegamento dipendente');
        }
      } else {
        // Unlink employee - update user to remove employee_id
        const res = await fetch(`${API_URL}/api/admin/users/${userId}/unlink-employee`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.detail || 'Errore scollegamento dipendente');
        }
      }

      return true;
    } catch (err) {
      alert(err.message);
      return false;
    }
  };

  const saveEditedUser = async () => {
    if (!editingUser) return;
    
    let success = true;
    
    // Update role if changed
    if (editingUser.newRole !== editingUser.role) {
      await handleChangeRole(editingUser._id, editingUser.newRole);
    }
    
    // Update employee link if changed
    const currentEmployeeId = editingUser.employee_id || '';
    const newEmployeeId = editingUser.newEmployeeId || '';
    
    if (newEmployeeId !== currentEmployeeId) {
      success = await handleLinkEmployee(editingUser._id, newEmployeeId);
    }
    
    if (success) {
      setShowEditModal(false);
      setEditingUser(null);
      fetchData();
    }
  };

  if (loading) {
    return (
      <div className="loading" data-testid="loading">
        <p>Caricamento utenti...</p>
      </div>
    );
  }

  return (
    <div data-testid="users-management-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Gestione Utenti</h1>
          <p className="page-description">
            Approva registrazioni e gestisci ruoli utenti
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-2 flex-wrap">
        <button
          className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('all')}
          data-testid="filter-all"
        >
          Tutti ({counts.all})
        </button>
        <button
          className={`btn ${filter === 'pending' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('pending')}
          data-testid="filter-pending"
        >
          In Attesa ({counts.pending})
        </button>
        <button
          className={`btn ${filter === 'active' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('active')}
          data-testid="filter-active"
        >
          Attivi ({counts.active})
        </button>
        <button
          className={`btn ${filter === 'rejected' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('rejected')}
          data-testid="filter-rejected"
        >
          Rifiutati ({counts.rejected})
        </button>
      </div>

      {/* Users Table */}
      {users.length === 0 ? (
        <div className="empty-state">
          <Users className="empty-state-icon" />
          <p>Nessun utente trovato</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table data-testid="users-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Ruolo</th>
                  <th>Stato</th>
                  <th>Data Registrazione</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user._id} data-testid="user-row">
                    <td className="font-medium">
                      {user.nome} {user.cognome}
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <span className="badge badge-secondary">
                        {user.role === 'admin' && '👑 Admin'}
                        {user.role === 'hse_manager' && '🛡️ HSE Manager'}
                        {user.role === 'employee' && '👤 Dipendente'}
                      </span>
                    </td>
                    <td>
                      {user.status === 'pending' && (
                        <span className="badge badge-warning">In Attesa</span>
                      )}
                      {user.status === 'active' && (
                        <span className="badge badge-ok">Attivo</span>
                      )}
                      {user.status === 'rejected' && (
                        <span className="badge badge-critical">Rifiutato</span>
                      )}
                    </td>
                    <td>
                      {user.created_at
                        ? format(new Date(user.created_at), 'dd MMM yyyy', { locale: it })
                        : '-'}
                    </td>
                    <td>
                      <div className="flex gap-2 flex-wrap">
                        {user.status === 'pending' && (
                          <>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => openApprovalModal(user)}
                              data-testid="approve-user"
                              title="Approva"
                            >
                              <Check size={16} />
                              Approva
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleReject(user._id)}
                              data-testid="reject-user"
                              title="Rifiuta"
                            >
                              <X size={16} />
                              Rifiuta
                            </button>
                          </>
                        )}
                        {user.status === 'active' && (
                          <>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => openEditModal(user)}
                              data-testid="edit-user"
                              title="Modifica Ruolo"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleResetPassword(user._id, `${user.nome} ${user.cognome}`)}
                              data-testid="reset-password"
                              title="Reset Password"
                            >
                              <Key size={16} />
                            </button>
                            <button
                              className="btn btn-secondary btn-sm text-red-500 hover:bg-red-50"
                              onClick={() => handleDelete(user._id, `${user.nome} ${user.cognome}`)}
                              data-testid="delete-user"
                              title="Elimina Utente"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                        {user.status === 'rejected' && (
                          <>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => openApprovalModal(user)}
                              data-testid="approve-user"
                              title="Approva"
                            >
                              <Check size={16} />
                              Riabilita
                            </button>
                            <button
                              className="btn btn-secondary btn-sm text-red-500 hover:bg-red-50"
                              onClick={() => handleDelete(user._id, `${user.nome} ${user.cognome}`)}
                              data-testid="delete-user"
                              title="Elimina Utente"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-lg w-full p-6">
            <h2 className="text-xl font-semibold mb-4">
              Approva Utente
            </h2>

            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-2">
                Stai per approvare:
              </p>
              <p className="font-medium">
                {selectedUser.nome} {selectedUser.cognome}
              </p>
              <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
            </div>

            <div className="form-group mb-4">
              <label className="form-label">Assegna Ruolo</label>
              <select
                className="form-input"
                value={selectedUser.selectedRole}
                onChange={(e) =>
                  setSelectedUser({ ...selectedUser, selectedRole: e.target.value })
                }
                data-testid="select-role"
              >
                <option value="employee">Dipendente</option>
                <option value="hse_manager">HSE Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div className="form-group mb-6">
              <label className="form-label">Collega a Dipendente (opzionale)</label>
              <select
                className="form-input"
                value={selectedUser.selectedEmployeeId}
                onChange={(e) =>
                  setSelectedUser({ ...selectedUser, selectedEmployeeId: e.target.value })
                }
                data-testid="select-employee"
              >
                <option value="">Nessun collegamento</option>
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.nome} {emp.cognome} - {emp.numero_matricola || emp.email}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Collegando l'account a un dipendente, l'utente potrà vedere i suoi documenti e scadenze
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                className="btn btn-secondary"
                onClick={() => setShowApprovalModal(false)}
              >
                Annulla
              </button>
              <button
                className="btn btn-primary"
                onClick={() =>
                  handleApprove(
                    selectedUser._id,
                    selectedUser.selectedRole,
                    selectedUser.selectedEmployeeId
                  )
                }
                data-testid="confirm-approve"
              >
                <Check size={16} />
                Conferma Approvazione
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-lg w-full p-6">
            <h2 className="text-xl font-semibold mb-4">
              <Edit size={20} className="inline mr-2" />
              Modifica Utente
            </h2>

            <div className="mb-4">
              <p className="font-medium">
                {editingUser.nome} {editingUser.cognome}
              </p>
              <p className="text-sm text-muted-foreground">{editingUser.email}</p>
            </div>

            <div className="form-group mb-4">
              <label className="form-label">Ruolo</label>
              <select
                className="form-input"
                value={editingUser.newRole}
                onChange={(e) =>
                  setEditingUser({ ...editingUser, newRole: e.target.value })
                }
                data-testid="edit-role-select"
              >
                <option value="employee">👤 Dipendente</option>
                <option value="hse_manager">🛡️ HSE Manager</option>
                <option value="admin">👑 Admin</option>
              </select>
            </div>

            <div className="form-group mb-6">
              <label className="form-label">Collega a Dipendente</label>
              <select
                className="form-input"
                value={editingUser.newEmployeeId}
                onChange={(e) =>
                  setEditingUser({ ...editingUser, newEmployeeId: e.target.value })
                }
                data-testid="edit-employee-select"
              >
                <option value="">❌ Nessun collegamento</option>
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.nome} {emp.cognome} - {emp.numero_matricola || emp.email}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Collegando l'account a un dipendente, l'utente potrà vedere i suoi documenti e scadenze
              </p>
              {editingUser.employee_id && (
                <p className="text-xs text-primary mt-1">
                  ✓ Attualmente collegato a: {employees.find(e => e._id === editingUser.employee_id)?.nome} {employees.find(e => e._id === editingUser.employee_id)?.cognome}
                </p>
              )}
            </div>

            <div className="flex gap-3 justify-end">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingUser(null);
                }}
              >
                Annulla
              </button>
              <button
                className="btn btn-primary"
                onClick={saveEditedUser}
                data-testid="save-edit"
              >
                <Check size={16} />
                Salva Modifiche
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersManagement;
